import {useCallback, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useGetStatusesQuery} from '../../../store/features/Team/teamService';
import type {UserElement} from '../../../store/features/Users/userService';
import type {PrepPerson} from './PrepDialog';
import {teamUserId, teamUserName} from './PrepAddPeople';
import {PRESENCE_TEXT, presenceOf} from './usePrepPresence';

const ADDED_PREFIX = 'added:';

export const isAddedPersonId = (id: string): boolean => id.startsWith(ADDED_PREFIX);

export interface AddedPeople {
    readonly users: readonly UserElement[];
    readonly ids: ReadonlySet<string>;
    /** Rows for the Prep list; they are selected, and unticking one removes it. */
    readonly people: readonly PrepPerson[];
    readonly add: (user: UserElement) => void;
    readonly remove: (personId: string) => void;
}

/** People picked from "Add people from Team" in Prep (D-CONF-03). */
export const useAddedPeople = (): AddedPeople => {
    const intl = useIntl();
    const [users, setUsers] = useState<readonly UserElement[]>([]);
    const userIds = useMemo(() => users.map(user => Number(teamUserId(user))).filter(Number.isFinite), [users]);
    const {data: statuses} = useGetStatusesQuery({users: userIds}, {skip: userIds.length === 0});

    const add = useCallback((user: UserElement) => setUsers(current =>
        current.some(other => teamUserId(other) === teamUserId(user)) ? current : [...current, user]), []);
    const remove = useCallback((personId: string) => setUsers(current =>
        current.filter(user => `${ADDED_PREFIX}${teamUserId(user)}` !== personId)), []);

    const people = useMemo(() => {
        const byUserId = new Map((statuses ?? []).map(status => [String(status.userId), status]));
        return users.map((user): PrepPerson => ({
            id: `${ADDED_PREFIX}${teamUserId(user)}`,
            identity: {name: teamUserName(user), userId: teamUserId(user), avatarUrl: user.avatarUrl || undefined},
            presence: intl.formatMessage(PRESENCE_TEXT[presenceOf(byUserId.get(teamUserId(user)))]),
            selected: true,
            selectable: true,
        }));
    }, [intl, statuses, users]);

    const ids = useMemo(() => new Set(users.map(teamUserId)), [users]);
    return {users, ids, people, add, remove};
};
